import React, { useState } from 'react';
import contactImg1 from "../../assets/images/contact1.png";
import FadeIn from "../common/FadeIn";
import { submitInquiryForm } from '../../utils/api';

const InquiryFormSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    location: '',
    howFound: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      let isStillError = false;
      if (name === 'email') {
        isStillError = !value.trim() || !/\S+@\S+\.\S+/.test(value.trim());
      } else {
        isStillError = !value.trim();
      }

      if (!isStillError) {
        setErrors({});
      }
    }
  };

  const validateSequential = () => {
    const fieldsOrder = ['name', 'email', 'location', 'howFound', 'message'];
    const newErrors = {};

    for (const field of fieldsOrder) {
      if (field === 'email') {
        const isValidEmail = formData.email.trim() && /\S+@\S+\.\S+/.test(formData.email);
        if (!isValidEmail) {
          newErrors.email = true;
          break;
        }
      } else {
        if (!formData[field].trim()) {
          newErrors[field] = true;
          break;
        }
      }
    }

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validateSequential();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    const initialFormState = {
      name: '',
      email: '',
      location: '',
      howFound: '',
      message: ''
    };

    submitInquiryForm(formData)
      .then(() => {
        setIsSubmitting(false);
        setIsSubmitted(true);
        setFormData(initialFormState);
      })
      .catch((err) => {
        console.error('FormSubmit submit error:', err);
        setIsSubmitting(false);
        setIsSubmitted(true);
        setFormData(initialFormState);
      });
  };

  const getInputStyle = (fieldName) => {
    const baseStyle =
      "font-interDisplayMedium w-full md:w-full max-w-[360px] text-[14px] h-[36px] md:h-[60px] bg-transparent outline-none transition-all leading-[1.6] tracking-[0em] md:text-base font-normal px-2.5";

    if (errors[fieldName]) {
      return `${baseStyle} border border-red-500 focus:border-red-500`;
    }
    return `${baseStyle} border-b border-[#ccc3ad] focus:border-[#2C2623]`;
  };

  return (
    <>
      <div className="relative w-full min-h-[680px] md:min-h-[740px] pt-6 md:pb-14">
        <FadeIn className="w-[220px] sm:w-[260px] aspect-[3/4] mx-auto mb-6 sm:mb-8 md:mb-0 md:w-[260px] md:aspect-[3/4] md:absolute md:-left-[257px] lg:-left-[50px] min-[1024px]:-left-[200px] md:top-4 z-10 transition-transform border-[6px] md:border-[10px] border-[#f0ece1] rounded-none shadow-md">
          <img
            src={contactImg1}
            alt="SeaSalt Creative Inquiry & Consultation Booking"
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
            width="260"
            height="346"
          />
        </FadeIn>

        <div className="w-full md:max-w-[708px] text-center z-10 px-2 lg:px-0 mx-auto">
          <FadeIn>
            <h1 className="font-interDisplayMedium text-center leading-[1.2] tracking-[0.06em] text-[11px] md:text-[14px] font-semibold mb-[26px] md:mb-[59px]">
              INQUIRE
            </h1>

            {isSubmitted ?
              <p className="font-saolDisplayItalic leading-[1.3] text-center tracking-[0em] font-normal uppercase text-[34px] md:text-[100px] mb-[29px]">
                YOUR MESSAGE IS ON ITS WAY.
              </p>
              :
              <h2 className="font-saolDisplayItalic leading-[1.3] text-center tracking-[0em] font-normal text-[24px] md:text-[40px] md:mb-[60px]">
                Tell me about your business and the website you have in mind. I will take it from there.
              </h2>
            }
          </FadeIn>

          {isSubmitted ? (
            <div className="flex flex-col items-center justify-center gap-6 mt-8">
              <p className="font-interDisplayMedium leading-[1.3] text-center tracking-[0em] text-[20px] md:text-[24px] max-w-[550px] mx-auto text-[#1C1917] font-normal">
                Thanks for reaching out. I will get back to you within 48 hours with the next steps and everything you need to get started.
              </p>
              <button
                type="button"
                onClick={() => setIsSubmitted(false)}
                className="p-[16px_18px_15px_18px] bg-[#ce884d] text-[#ffffff] font-montserratMedium text-[9px] lg:text-[11px] font-semibold leading-[1] tracking-[0.06em] cursor-pointer duration-[0.5s] hover:bg-[#ccc3ad] hover:text-black w-[260px] h-[50px] md:w-[201px] md:h-[60px] uppercase"
              >
                CLOSE
              </button>
            </div>
          ) : (
            <FadeIn className="w-full max-w-[700px] mx-auto mt-8 sm:mt-12 md:mt-16">
              <form onSubmit={handleSubmit} noValidate className="space-y-7 sm:space-y-9 text-center">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-7 sm:gap-10">
                  <div className="relative">
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Full Name"
                      className={getInputStyle('name')}
                    />
                  </div>
                  <div className="relative">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Email"
                      className={getInputStyle('email')}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-7 sm:gap-10">
                  <div className="relative">
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="City & State"
                      className={getInputStyle('location')}
                    />
                  </div>

                  <div className="relative">
                    <input
                      type="text"
                      name="howFound"
                      value={formData.howFound}
                      onChange={handleChange}
                      placeholder="Tell me about your website"
                      className={getInputStyle('howFound')}
                    />
                  </div>
                </div>

                <div className="relative">
                  <textarea
                    name="message"
                    rows="10"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="What do you need?"
                    className={`${getInputStyle('message')} md:!w-full md:!max-w-full resize-none min-h-[60px] md:min-h-[80px]`}
                  />
                </div>

                <div className="pt-[15px] text-center">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="p-[16px_18px_15px_18px] bg-[#ce884d] text-[#ffffff] font-montserratMedium text-[9px] lg:text-[11px] font-semibold leading-[1] tracking-[0.06em] cursor-pointer duration-[0.5s] hover:bg-[#ccc3ad] hover:text-black w-[260px] h-[50px] md:w-[201px] md:h-[60px] disabled:opacity-60 disabled:cursor-not-allowed uppercase"
                  >
                    {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
                  </button>
                </div>
              </form>
            </FadeIn>
          )}
        </div>
      </div>
    </>
  );
};

export default InquiryFormSection;
